import { ApolloClient, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";

/**
 * Fetch implementation that reports upload progress.
 *
 * The browser `fetch` API cannot expose upload progress, so for requests that
 * pass an `onUploadProgress` callback (via Apollo `context.fetchOptions`) we use
 * `XMLHttpRequest` instead. All other requests fall through to native `fetch`,
 * leaving the rest of the GraphQL traffic untouched.
 */
const progressFetch: typeof fetch = (input, init) => {
  const options = init as (RequestInit & {
    onUploadProgress?: (percent: number) => void;
  }) | undefined;

  if (typeof options?.onUploadProgress !== "function") {
    return fetch(input as RequestInfo, init);
  }

  const uri = String(input);
  const { onUploadProgress } = options;

  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || "POST", uri, true);

    const headers = options.headers as Record<string, string> | undefined;
    if (headers) {
      Object.keys(headers).forEach((key) =>
        xhr.setRequestHeader(key, headers[key]),
      );
    }

    xhr.onload = () => {
      const responseHeaders = new Headers();
      xhr
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)
        .forEach((line) => {
          const idx = line.indexOf(": ");
          if (idx > 0)
            responseHeaders.set(line.slice(0, idx), line.slice(idx + 2));
        });
      resolve(
        new Response(xhr.response, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: responseHeaders,
        }),
      );
    };

    xhr.onerror = () => reject(new TypeError("Network request failed"));
    xhr.ontimeout = () => reject(new TypeError("Network request timed out"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));

    if (xhr.upload) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          onUploadProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    const signal = options.signal;
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.send(options.body as XMLHttpRequestBodyInit);
  });
};

const uploadLink = createUploadLink({
  uri:
    process.env.REACT_APP_ENV === "development"
      ? process.env.REACT_APP_API_URI
      : process.env.REACT_APP_API_SERVER_URI,
  fetch: progressFetch,
});

/**
 * The server force-logs-out a session by invalidating its JWT (per-user or
 * global token-version bump). The JWT strategy then rejects requests with a
 * 401 carrying code "SESSION_REVOKED". In dev that code is reachable at
 * `extensions.exception.response.code`; in production Apollo strips
 * `extensions.exception`, so we also match on the stable message. On a match we
 * clear the stored session and send the user to login — a full reload makes the
 * store re-read the (now empty) `userInfo`, so the route guards treat them as
 * logged out.
 */
const SESSION_REVOKED_MESSAGE = "Session ended, please log in again.";
let handlingSessionRevoked = false;
let handlingPasswordChange = false;

const errorCodeOf = (err: any): string | undefined => {
  const exception = (err.extensions as any)?.exception;
  return exception?.response?.code ?? exception?.code;
};

const errorLink = onError(({ graphQLErrors }) => {
  if (!graphQLErrors) return;
  const revoked = graphQLErrors.some(
    (err) =>
      errorCodeOf(err) === "SESSION_REVOKED" ||
      err.message === SESSION_REVOKED_MESSAGE,
  );
  if (revoked && !handlingSessionRevoked) {
    handlingSessionRevoked = true;
    localStorage.removeItem("userInfo");
    window.location.assign("/login");
    return;
  }
  // Defense-in-depth: a valid token whose user is flagged for a forced password
  // change is rejected on every guarded call. Keep the stored user (the
  // change screen needs their id) and send them to the locked screen.
  const mustChange = graphQLErrors.some(
    (err) => errorCodeOf(err) === "PASSWORD_CHANGE_REQUIRED",
  );
  // Already on the locked screen: do nothing. Background polling
  // (heartbeat/notifications) keeps erroring here, and re-assigning would
  // trigger a full reload loop. The route guard already keeps the user put.
  if (
    mustChange &&
    !handlingPasswordChange &&
    window.location.pathname !== "/force-change-password"
  ) {
    handlingPasswordChange = true;
    // Persist the flag so the route guard keeps the user on the locked screen
    // (the flag may have been set server-side after this session started).
    const stored = localStorage.getItem("userInfo");
    if (stored) {
      const userInfo = JSON.parse(stored);
      userInfo.mustChangePassword = true;
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
    }
    window.location.assign("/force-change-password");
  }
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  // return the headers to the context so httpLink can read them
  //const { user } = useAppSelector(selectAuth);
  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") as string)
    : null;
  const token = userInfo ? userInfo.token : null;
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(uploadLink),
  cache: new InMemoryCache(),
  connectToDevTools: true,
});
